import { Button, Expandable, expandableChevronSx, Text } from '@components/ui';
import { useCategoriesQuery } from '@lib/api/categories';
import { errorBody } from '@lib/api/http';
import {
  fetchProfile,
  useProfileQuery,
  useUpdateExpertiseMutation,
  useUpdateLevelMutation,
} from '@lib/api/profile';
import { queryKeys } from '@lib/api/queryKeys';
import { ExpandMore } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Slider from '@mui/material/Slider';
import { alpha, type Theme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import type { ExpertiseLevel } from '../level-of-expertise/levelOfExpertiseTypes';
import type {
  ExpertiseCategory,
  ExpertiseLocationState,
  MutationResponse,
} from './areaOfExpertiseTypes';


const SKELETON_ROWS = [0, 1, 2, 3, 4, 5];

const sliderSx = {
  mx: 0.5,
  color: 'text.primary',
  height: 4,
  '& .MuiSlider-rail': {
    opacity: 1,
    bgcolor: 'action.hover',
  },
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    boxShadow: 'none',
    '&:hover, &.Mui-focusVisible': { boxShadow: 'none' },
  },
  '& .MuiSlider-mark': {
    width: 4,
    height: 4,
    borderRadius: '50%',
    bgcolor: 'text.disabled',
  },
  '& .MuiSlider-markActive': {
    bgcolor: 'background.paper',
  },
};

const levelPillSx = (rated: boolean) => ({
  px: 1,
  py: 0.25,
  borderRadius: 999,
  border: 1,
  borderColor: rated ? 'text.primary' : 'divider',
  bgcolor: (theme: Theme) =>
    rated
      ? alpha(
          theme.palette.text.primary,
          theme.palette.mode === 'dark' ? 0.2 : 0.08
        )
      : 'transparent',
  color: rated ? 'text.primary' : 'text.secondary',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '18px',
  whiteSpace: 'nowrap' as const,
});

const skillHighlightBg = (theme: Theme, rated: boolean): string => {
  if (theme.palette.mode === 'dark') {
    return rated ? alpha('#000', 0.5) : alpha('#000', 0.32);
  }
  return alpha(theme.palette.text.primary, rated ? 0.1 : 0.05);
};

const SWITCH_WIDTH = 36;
const SWITCH_HEIGHT = 20;
const SWITCH_INSET = 2;
const SWITCH_THUMB = SWITCH_HEIGHT - SWITCH_INSET * 2;

const switchSx = {
  width: SWITCH_WIDTH,
  height: SWITCH_HEIGHT,
  p: 0,
  flexShrink: 0,
  '& .MuiSwitch-switchBase': {
    p: `${SWITCH_INSET}px`,
    color: 'text.primary',
    '&.Mui-checked': {
      transform: `translateX(${SWITCH_WIDTH - SWITCH_HEIGHT}px)`,
      color: 'primary.contrastText',
    },
    '&.Mui-checked + .MuiSwitch-track': {
      bgcolor: 'primary.main',
      opacity: 1,
    },
  },
  '& .MuiSwitch-thumb': {
    width: SWITCH_THUMB,
    height: SWITCH_THUMB,
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: SWITCH_HEIGHT / 2,
    bgcolor: 'action.selected',
    opacity: 1,
  },
};

interface AreaOfExpertiseProps {
  embedded?: boolean;
}

interface ProfileWithLevels {
  data?: {
    levelOfExpertise?: ExpertiseLevel[];
  };
}

const sameId = (left: string | number, right: string | number): boolean =>
  String(left) === String(right);

const copyLevels = (levels: ExpertiseLevel[]): ExpertiseLevel[] =>
  levels.map((level) => ({
    id: level.id,
    key: level.key,
    value: level.value,
    selectedValue: Number(level.selectedValue ?? 0),
  }));

const mergeMetadataIds = (
  levels: ExpertiseLevel[],
  fromProfile: ExpertiseLevel[]
): ExpertiseLevel[] =>
  levels.map((level) => {
    if (level.id != null && String(level.id) !== '') {
      return level;
    }
    const match = fromProfile.find((item) => sameId(item.value, level.value));
    return match?.id != null ? { ...level, id: match.id } : level;
  });

const selectionSnapshot = (ids: Array<string | number>): string =>
  JSON.stringify([...ids].map(String).sort());

const levelSnapshot = (levels: ExpertiseLevel[]): string =>
  JSON.stringify(
    [...levels]
      .map((level) => ({
        value: String(level.value),
        selectedValue: Number(level.selectedValue ?? 0),
      }))
      .sort((left, right) => left.value.localeCompare(right.value))
  );

const levelWord = (value: number): 'beginner' | 'intermediate' | 'expert' => {
  if (value <= 2) {
    return 'beginner';
  }
  if (value >= 6) {
    return 'expert';
  }
  return 'intermediate';
};

const ExpertiseListSkeleton = (): JSX.Element => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {SKELETON_ROWS.map((row) => (
      <Skeleton
        key={row}
        variant="rectangular"
        height={48}
        sx={{ bgcolor: 'action.hover', borderRadius: 2 }}
      />
    ))}
  </Box>
);

const AreaOfExpertise = ({
  embedded = false,
}: AreaOfExpertiseProps): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const massage = (location.state as ExpertiseLocationState | null)?.massage;
  const [isReadonly, setIsReadonly] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<
    string | number | null
  >(null);
  const [selectedSubCategories, setSelectedSubCategories] = useState<
    Array<string | number>
  >([]);
  const [savedIds, setSavedIds] = useState<Array<string | number>>([]);
  const [levels, setLevels] = useState<ExpertiseLevel[]>([]);
  const [savedLevels, setSavedLevels] = useState<ExpertiseLevel[]>([]);
  const expectedLevelsRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (massage) {
      toast.error(massage);
    }
  }, [massage]);

  const queryClient = useQueryClient();
  const categoriesQuery = useCategoriesQuery();
  const allCategories = (categoriesQuery.data ?? []) as ExpertiseCategory[];
  const categoriesLoading = categoriesQuery.isLoading;
  const { data: profileResult, isSuccess: profileReady } = useProfileQuery();
  const userDetails = (profileResult ?? {}) as ProfileWithLevels;
  const profileLevels = userDetails.data?.levelOfExpertise;
  const updateExpertise = useUpdateExpertiseMutation();
  const updateLevel = useUpdateLevelMutation();

  useEffect(() => {
    if (!profileReady) {
      return;
    }
    const nextLevels = copyLevels(profileLevels ?? []);
    const incoming = levelSnapshot(nextLevels);
    if (expectedLevelsRef.current && expectedLevelsRef.current !== incoming) {
      return;
    }
    expectedLevelsRef.current = null;
    if (dirtyRef.current) {
      return;
    }
    setSelectedSubCategories(nextLevels.map((level) => level.value));
    setSavedIds(nextLevels.map((level) => level.value));
    setLevels(nextLevels);
    setSavedLevels(copyLevels(nextLevels));
    setIsReadonly(nextLevels.length > 0);
  }, [profileLevels, profileReady]);

  const handleFormEnabled = (): void => {
    if (isReadonly) {
      setIsReadonly(false);
      return;
    }
    setSelectedSubCategories([...savedIds]);
    setLevels(copyLevels(savedLevels));
    dirtyRef.current = false;
    setIsReadonly(true);
  };

  const handleOpenSubCategories = (categoryId: string | number): void => {
    setSelectedCategory((current) =>
      current != null && sameId(current, categoryId) ? null : categoryId
    );
  };

  const handleSelectSubCategory = (
    subCategoryId: string | number,
    name?: string
  ): void => {
    dirtyRef.current = true;
    const selected = selectedSubCategories.some((id) =>
      sameId(id, subCategoryId)
    );
    if (selected) {
      setSelectedSubCategories((current) =>
        current.filter((id) => !sameId(id, subCategoryId))
      );
      setLevels((current) =>
        current.filter((level) => !sameId(level.value, subCategoryId))
      );
      return;
    }
    setSelectedSubCategories((current) => [...current, subCategoryId]);
    setLevels((current) => [
      ...current,
      { value: subCategoryId, key: name, selectedValue: 0 },
    ]);
  };

  const handleRate = (subCategoryId: string | number, value: number): void => {
    dirtyRef.current = true;
    setLevels((current) =>
      current.map((level) =>
        sameId(level.value, subCategoryId)
          ? { ...level, selectedValue: value }
          : level
      )
    );
  };

  const finishSave = (
    data: MutationResponse,
    payload: ExpertiseLevel[]
  ): void => {
    if (!data.status) {
      toast.error(data.message);
      return;
    }
    expectedLevelsRef.current = levelSnapshot(payload);
    dirtyRef.current = false;
    setSavedIds([...selectedSubCategories]);
    setSavedLevels(copyLevels(payload));
    setLevels(copyLevels(payload));
    setIsReadonly(true);
    toast.success(data.message);
  };

  const handleSubmit = (): void => {
    const payload = levels.filter((level) =>
      selectedSubCategories.some((id) => sameId(id, level.value))
    );
    const selectionChanged =
      selectionSnapshot(selectedSubCategories) !== selectionSnapshot(savedIds);
    const ratingsChanged =
      levelSnapshot(payload) !== levelSnapshot(savedLevels);

    const saveRatings = (levelsToSave: ExpertiseLevel[]): void => {
      updateLevel.mutate(levelsToSave, {
        onSuccess: (result) =>
          finishSave(result as MutationResponse, levelsToSave),
        onError: (error: unknown) =>
          finishSave(errorBody(error), levelsToSave),
      });
    };

    const resolveIdsThenSaveRatings = async (
      levelsToSave: ExpertiseLevel[]
    ): Promise<void> => {
      const needsIds = levelsToSave.some(
        (level) => level.id == null || String(level.id) === ''
      );
      let withIds = levelsToSave;
      if (needsIds) {
        const profileBody = (await queryClient.fetchQuery({
          queryKey: queryKeys.profile,
          queryFn: fetchProfile,
        })) as ProfileWithLevels;
        withIds = mergeMetadataIds(
          levelsToSave,
          profileBody.data?.levelOfExpertise ?? []
        );
      }
      if (
        withIds.some((level) => level.id == null || String(level.id) === '')
      ) {
        finishSave(
          {
            status: false,
            message: 'Missing expertise id; refresh and try again.',
          },
          levelsToSave
        );
        return;
      }
      setLevels(copyLevels(withIds));
      saveRatings(withIds);
    };

    if (!selectionChanged) {
      void resolveIdsThenSaveRatings(payload);
      return;
    }

    updateExpertise.mutate(selectedSubCategories, {
      onSuccess: (result) => {
        void (async () => {
          const response = result as MutationResponse;
          if (!response.status || !ratingsChanged) {
            finishSave(response, payload);
            return;
          }
          // Area save creates UserMetaData rows — refetch to get their ids.
          await queryClient.invalidateQueries({ queryKey: queryKeys.profile });
          await resolveIdsThenSaveRatings(payload);
        })();
      },
      onError: (error: unknown) => finishSave(errorBody(error), payload),
    });
  };

  const canEdit = savedIds.length > 0;
  const selectedPayload = levels.filter((level) =>
    selectedSubCategories.some((id) => sameId(id, level.value))
  );
  const changed =
    selectionSnapshot(selectedSubCategories) !== selectionSnapshot(savedIds) ||
    levelSnapshot(selectedPayload) !== levelSnapshot(savedLevels);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        component="form"
        onSubmit={(event) => event.preventDefault()}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {embedded ? null : (
                <Text as="h2" variant="subtitle" sx={{ m: 0 }}>
                  {t('nav.areasOfExpertise')}
                </Text>
              )}
              <Text variant="caption" tone="muted">
                {t('profile.rateHint')}
              </Text>
            </Box>
            {canEdit ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleFormEnabled}
              >
                {isReadonly ? t('common.edit') : t('common.cancel')}
              </Button>
            ) : null}
          </Box>
          <Box sx={{ p: 2 }}>
            {categoriesLoading ? (
              <ExpertiseListSkeleton />
            ) : (
              allCategories.map((category) => (
                <CategoryGroup
                  key={category.id}
                  category={category}
                  open={
                    selectedCategory != null &&
                    sameId(selectedCategory, category.id)
                  }
                  readonly={isReadonly}
                  selectedIds={selectedSubCategories}
                  levels={levels}
                  onToggleGroup={handleOpenSubCategories}
                  onToggleSkill={handleSelectSubCategory}
                  onRate={handleRate}
                />
              ))
            )}
          </Box>
        </Box>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={isReadonly || !changed || selectedSubCategories.length < 1}
          loading={updateExpertise.isPending || updateLevel.isPending}
          type="button"
          onClick={handleSubmit}
        >
          {t('common.update')}
        </Button>
      </Box>
    </Box>
  );
};

interface CategoryGroupProps {
  category: ExpertiseCategory;
  open: boolean;
  readonly: boolean;
  selectedIds: Array<string | number>;
  levels: ExpertiseLevel[];
  onToggleGroup: (categoryId: string | number) => void;
  onToggleSkill: (subCategoryId: string | number, name?: string) => void;
  onRate: (subCategoryId: string | number, value: number) => void;
}

const CategoryGroup = ({
  category,
  open,
  readonly,
  selectedIds,
  levels,
  onToggleGroup,
  onToggleSkill,
  onRate,
}: CategoryGroupProps): JSX.Element => (
  <Box
    sx={{
      mb: 1.5,
      overflow: 'hidden',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      '&:last-of-type': { mb: 0 },
    }}
  >
    <Box
      onClick={() => onToggleGroup(category.id)}
      sx={{
        display: 'flex',
        minHeight: 48,
        cursor: 'pointer',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.75,
        py: 1.25,
      }}
    >
      <Text variant="label" sx={{ fontWeight: 500 }}>
        {category.name}
      </Text>
      <ExpandMore fontSize="small" sx={expandableChevronSx(open)} />
    </Box>
    <Expandable open={open}>
      {category.subCategories.map((subCategory) => {
        const selected = selectedIds.some((id) => sameId(id, subCategory.id));
        const level = levels.find((item) => sameId(item.value, subCategory.id));
        const value = Number(level?.selectedValue ?? 0);
        return (
          <SkillRow
            key={subCategory.id}
            name={subCategory.name ?? ''}
            selected={selected}
            readonly={readonly}
            value={value}
            onToggle={() => onToggleSkill(subCategory.id, subCategory.name)}
            onRate={(next) => onRate(subCategory.id, next)}
          />
        );
      })}
    </Expandable>
  </Box>
);

interface SkillRowProps {
  name: string;
  selected: boolean;
  readonly: boolean;
  value: number;
  onToggle: () => void;
  onRate: (value: number) => void;
}

const SkillRow = ({
  name,
  selected,
  readonly,
  value,
  onToggle,
  onRate,
}: SkillRowProps): JSX.Element => {
  const { t } = useTranslation();
  const rated = selected && value > 0;

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: (theme) =>
          selected ? skillHighlightBg(theme, rated) : 'transparent',
        borderLeft: 3,
        borderLeftColor: rated
          ? 'text.primary'
          : selected
            ? 'text.secondary'
            : 'transparent',
      }}
    >
      <Box
        component={readonly ? 'div' : 'label'}
        sx={{
          display: 'flex',
          minHeight: 52,
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 1.75,
          cursor: readonly ? 'default' : 'pointer',
          '&:hover': readonly
            ? undefined
            : {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? alpha('#000', 0.22)
                    : alpha(theme.palette.text.primary, 0.04),
              },
        }}
      >
        <Text
          variant="body"
          sx={{
            color: 'text.primary',
            opacity: selected ? 1 : 0.72,
            fontWeight: rated ? 700 : selected ? 600 : 400,
          }}
        >
          {name}
        </Text>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {selected ? (
            <Box component="span" sx={levelPillSx(rated)}>
              {t(`profile.${levelWord(value)}`)} · {value}
            </Box>
          ) : null}
          {readonly ? null : (
            <Switch
              size="small"
              checked={selected}
              onChange={onToggle}
              inputProps={{ 'aria-label': name }}
              sx={switchSx}
            />
          )}
        </Box>
      </Box>
      <Expandable open={selected && !readonly}>
        <Box sx={{ px: 1.75, pt: 0.5, pb: 1.5 }}>
          <Slider
            size="small"
            aria-label={t('profile.levelFor', { name })}
            value={value}
            valueLabelDisplay="off"
            onChange={(_event, next) => {
              if (typeof next !== 'number') {
                return;
              }
              onRate(next);
            }}
            step={1}
            marks
            min={0}
            max={8}
            sx={sliderSx}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text variant="caption" tone="muted">
              {t('profile.beginner')}
            </Text>
            <Text variant="caption" tone="muted">
              {t('profile.expert')}
            </Text>
          </Box>
        </Box>
      </Expandable>
    </Box>
  );
};

export default AreaOfExpertise;
